import React from 'react';
import { useBackdropDismiss } from '../../utils/backdropDismiss';
import { formatMoney } from '../../utils/formatMoney';
import { useTranslation } from 'react-i18next';
import type { Product, ProductSize } from '../../types/order';
import { sizeChannelPrice } from '../../utils/pricing';

interface ProductSizeModalProps {
  product: Product;
  /** Currently selected size (null in `immediate` mode or before user picks) */
  selectedSize: ProductSize | null;
  /**
   * Called when user selects a size.
   * - In `immediate` mode, the parent should treat this as a confirm (add to basket immediately).
   * - In `select-confirm` mode, the parent should just store the size.
   */
  onSelectSize: (size: ProductSize) => void;
  /** Called when user clicks the bottom "Add" button. Only used in `select-confirm` mode. */
  onConfirm?: () => void;
  /** Called when user cancels / clicks outside. */
  onClose: () => void;
  /**
   * UX mode:
   * - `immediate`: clicking a size is the confirm action (TakeAway-style)
   * - `select-confirm`: clicking highlights; a separate Add button confirms (Delivery-style)
   */
  mode: 'immediate' | 'select-confirm';
  /** Order channel — picks the per-size price override (size-only stores). */
  channel?: 'take_away' | 'delivery' | 'dine_in';
  translationNamespace?: string;
}

const ProductSizeModal: React.FC<ProductSizeModalProps> = ({
  product,
  selectedSize,
  onSelectSize,
  onConfirm,
  onClose,
  mode,
  channel = 'take_away',
  translationNamespace = 'takeaway',
}) => {
  const { t } = useTranslation([translationNamespace, 'common']);

  // Κάθε κουμπί δείχνει ΑΚΡΙΒΩΣ ό,τι θα μπει στο καλάθι: την απόλυτη τιμή του μεγέθους στο
  // κανάλι (lineUnitPrice του addToBasketWithSize σε κάθε οθόνη).
  const renderPrice = (size: ProductSize) => `${formatMoney(sizeChannelPrice(size, channel))}`;

  // Adapt modal width + grid columns to the size count so 2 sizes don't
  // float in a 5xl-wide modal. Tailwind JIT requires full literal class names.
  const sizeCount = product.sizes?.length || 0;
  const modalMaxWidth =
    sizeCount <= 1 ? 'max-w-md' :
    sizeCount === 2 ? 'max-w-2xl' :
    sizeCount === 3 ? 'max-w-3xl' :
    sizeCount === 4 ? 'max-w-4xl' :
    'max-w-5xl';
  const gridCols =
    sizeCount <= 1 ? 'grid-cols-1' :
    sizeCount === 2 ? 'grid-cols-2' :
    sizeCount === 3 ? 'grid-cols-2 sm:grid-cols-3' :
    sizeCount === 4 ? 'grid-cols-2 sm:grid-cols-4' :
    'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4';

  // Το φόντο κλείνει μόνο με πάτημα που ξεκίνησε ΣΤΟ φόντο (το click της αφής, 16/10/2026).
  const backdrop = useBackdropDismiss(onClose);

  return (
    <div
      className="cashier-overlay fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(4px)' }}
      {...backdrop}
    >
      <div
        className={`cashier-dialog bg-surface-1 rounded-2xl p-6 w-full ${modalMaxWidth} shadow-2xl border border-line`}
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-2xl font-bold text-t1 mb-1">{product.name}</h2>
        <p className="text-t3 text-sm mb-5">
          {t(`${translationNamespace}:selectSize`, {
            defaultValue: t(`${translationNamespace}:sizeSelectionTitle`, { defaultValue: 'Select size' }),
          })}
        </p>

        <div className={`grid ${gridCols} gap-3`}>
          {product.sizes?.map(size => {
            const isSelected = selectedSize?.id === size.id;
            return (
              <button
                key={size.id}
                onClick={() => onSelectSize(size)}
                className={`p-4 rounded-xl border-2 transition-all active:scale-[0.97] min-h-[120px] flex flex-col items-center justify-center text-center ${
                  mode === 'select-confirm' && isSelected
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-md'
                    : 'border-line-strong hover:border-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/20'
                }`}
              >
                <span className="font-bold text-xl text-t1">{size.name}</span>
                <span className="font-bold text-lg text-blue-600 dark:text-blue-400 mt-2">{renderPrice(size)}</span>
              </button>
            );
          })}
        </div>

        <div className={mode === 'select-confirm' ? 'flex gap-3 mt-5' : 'mt-5'}>
          <button
            onClick={onClose}
            className="flex-1 py-4 px-6 rounded-xl border-2 border-line-strong font-bold text-base text-t3 hover:bg-surface-2 active:scale-[0.98] transition-all"
          >
            {t(`${translationNamespace}:cancel`, { defaultValue: t('common:cancel', { defaultValue: 'Cancel' }) })}
          </button>
          {mode === 'select-confirm' && (
            <button
              onClick={onConfirm}
              disabled={!selectedSize}
              className="cashier-primary flex-[2] py-4 px-6 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:bg-surface-3 disabled:text-t3 text-white font-bold text-base active:scale-95 transition-all"
            >
              {t(`${translationNamespace}:addToCart`, {
                defaultValue: t(`${translationNamespace}:addToBasket`, { defaultValue: 'Add' }),
              })}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductSizeModal;
